<template>
  <div class="interviews-content" v-if="hasInterviewPermission">
    <div class="header-container">
      <span class="title">{{ $t(`${i18nPrefix}.containerTitle.interviews`) }}</span>
      <div class="switch-button">
        <i class="iconfont iconCalendar" @click="handelToInterview('')"></i>
        <SwitchButton
          v-model="interviewsType"
          :leftLabel="$t(`${i18nPrefix}.btn.my`)"
          leftLabelValue="my"
          :rightLabel="$t(`${i18nPrefix}.btn.all`)"
          rightLabelValue="all"
          @change="handelChangeTabStatus"
        />
      </div>
    </div>
    <div class="body-container">
      <div class="date-select-container">
        <span class="month-label">{{ monthLabel }}</span>
        <div class="date-select-body">
          <div
            class="date-list-item"
            v-for="(item, index) in dateSelectList"
            :key="index"
          >
            <span class="label">{{ item.label }}</span>
            <div class="day-container">
              <div
                class="day"
                :class="[item.select && 'select', item.number > 0 && 'has-number-icon']"
                @click="handelClickDate(item.date)">
                {{ item.day }}
                <div class="number-icon" v-if="item.number > 0">
                  <div class="icon show"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="interviews-list">
        <div
          class="interview-list-body"
          v-for="(interviewList, label, index) in interviewDetail"
          :key="index"
        >
          <span class="date-label">{{ label }}</span>
          <div
            class="interview-list-item"
            v-for="(item, index) in interviewList"
            :key="index"
            @click="handelToInterview(item.interview.jobName)"
          >
            <span class="interview-time">{{ item.interview.time }}</span>
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="item.interview.name"
            >
              <span class="candidate-time">{{ $t(`${i18nPrefix}.label.interview.candidateName`) }}{{ item.interview.name }}</span>
            </el-tooltip>
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="item.managers"
            >
              <span class="managers-name">{{ $t(`${i18nPrefix}.label.interview.interviewersName`) }}{{ item.managers }}</span>
            </el-tooltip>
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="`${item.interview[isEn ? 'jobName' : 'jobCnName']} / ${item.interview[isEn ? 'companyName' : 'companyCnName']}`"
            >
              <span class="job-info">{{ `${item.interview[isEn ? 'jobName' : 'jobCnName']} / ${item.interview[isEn ? 'companyName' : 'companyCnName']}` }}</span>
            </el-tooltip>
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
      </div>
    </div>
    <RectLoading class="loading" v-show="loading"/>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { isEn } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import { INTERVIEW_STATUS } from '@/constant/interview'
import RectLoading from '@/components/loading/rectLoading.vue'
import SwitchButton from './switchButton.vue'
import mixin from './mixin'

export default {
  mixins: [mixin],
  components: {
    SwitchButton,
    RectLoading
  },
  data () {
    return {
      i18nPrefix: 'admin.hrDashboard',
      interviewDetailCount: 5,
      loading: false,
      interviewsType: 'my',
      selectDate: moment().format('YYYY-MM-DD'),
      interviewData: {}
    }
  },
  computed: {
    isEn () {
      return isEn()
    },
    monthLabel () {
      const current = moment.locale()
      const locale = this.$i18n.locale === 'zh' ? 'zh-cn' : 'en'
      if (locale !== current) {
        moment.locale(locale)
      }
      return moment().format('MMMM')
    },
    dateSelectList () {
      const current = moment.locale()
      const locale = this.$i18n.locale === 'zh' ? 'zh-cn' : 'en'
      if (locale !== current) {
        moment.locale(locale)
      }
      const list = []
      _.forEach(_.range(1, 8), (item) => {
        const momentDate = moment().day(item)
        const interviewList = this.interviewData[momentDate.format('YYYY-MM-DD')]
        list.push({
          date: momentDate,
          label: momentDate.format('dd'),
          day: momentDate.format('DD'),
          select: momentDate.format('YYYY-MM-DD') === this.selectDate,
          number: interviewList ? interviewList.length : 0
        })
      })
      return list
    },
    interviewDetail () {
      const dateList = this.dateSelectList
      const interviewDetail = {}
      const selectDateMoment = moment(this.selectDate)
      let interviewCount = 0
      _.forEach(dateList, (item) => {
        if (item.select || selectDateMoment.isBefore(item.date)) {
          if (interviewCount > this.interviewDetailCount) {
            return false
          }
          let interviewList = []
          const timeLabel = item.date.format(isEn() ? 'MMM. Do' : 'MMMDo')
          if (_.has(interviewDetail, timeLabel)) {
            interviewList = interviewDetail[timeLabel]
          }
          const tempList = this.interviewData[item.date.format('YYYY-MM-DD')]
          if (tempList) {
            if (tempList.length + interviewCount > this.interviewDetailCount) {
              interviewList.push(..._.slice(tempList, 0, (this.interviewDetailCount - interviewCount)))
            } else {
              interviewList.push(...tempList)
            }
            interviewCount += tempList.length
          }
          if (interviewList && interviewList.length > 0) {
            interviewDetail[timeLabel] = interviewList
          }
        }
      })
      return interviewDetail
    }
  },
  created () {
    this.interviewsType = this.interviewsTabStatus()
    this.initData()
  },
  methods: {
    ...mapActions('hrDashboard', ['getInterviewInfo', 'changeInterviewsTabStatus']),
    ...mapGetters('hrDashboard', ['interviewsTabStatus']),
    ...mapActions('hm', ['hrAutoLoginHm', 'getHMInterviewRateList']),
    async initData () {
      this.loading = true
      try {
        const hmToken = await this.hrAutoLoginHm()
        let resp
        let interviewRecords = []
        if (hmToken && this.interviewsType === 'my') {
          const startTime = moment().day(1).format('YYYY-MM-DD')
          const endTime = moment().day(7).format('YYYY-MM-DD')
          resp = await this.getHMInterviewRateList({ start_day: startTime, end_day: endTime, status: '1,2', is_dashboard: true })
          if (resp) {
            interviewRecords = resp.interview_records
          }
        } else if (this.interviewsType === 'all') {
          resp = await this.getInterviewInfo({ my_interview: 0 })
          if (resp) {
            interviewRecords = resp.data
          }
        }
        const dataInfo = {}
        _.forEach(interviewRecords, (item, index) => {
          const interviewMoment = moment(item.interview_time)
          const interviewEndMoment = moment(item.interview_end_time)
          let list = []
          const timeKey = interviewMoment.format('YYYY-MM-DD')
          if (_.has(dataInfo, timeKey)) {
            list = dataInfo[timeKey]
          }
          list.push({
            interview: {
              date: interviewMoment,
              name: item.candidate_names,
              time: `${interviewMoment.format('HH:mm')}-${interviewEndMoment.format('HH:mm')}`,
              jobName: item.job_title,
              jobCnName: item.job_cn_title,
              companyName: item.company_name,
              companyCnName: item.company_cn_name
            },
            recruiter: item.creator.full_name,
            managers: item.hm_names,
            status: item.status
          })
          dataInfo[timeKey] = list
        })
        this.interviewData = dataInfo
      } finally {
        this.loading = false
      }
    },
    handelClickDate (momentDate) {
      if (!moment.isMoment(momentDate)) {
        return
      }
      const selectDate = momentDate.format('YYYY-MM-DD')
      if (selectDate !== this.selectDate) {
        this.selectDate = selectDate
      }
    },
    handelToInterview (keyword) {
      let path = '/v-admin/interview-management'
      if (keyword) {
        path += `?keyword=${keyword}&status=${INTERVIEW_STATUS.SCHEDULED},${INTERVIEW_STATUS.PENDING}&list_type=TABLE`
      }
      this.$router.push(path)
    },
    handelChangeTabStatus () {
      this.changeInterviewsTabStatus(this.interviewsType)
    }
  },
  watch: {
    interviewsType: {
      immediate: true,
      handler () {
        if (!this.loading) {
          this.initData()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.interviews-content {
  background: #FFFFFF;
  border-radius: 6px;
  color: #303133;
  font-weight: 400;
  margin-bottom: 15px;
  position: relative;

  .header-container {
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-size: 18px;
      height: 25px;
      line-height: 25px;
      font-weight: bold;
    }

    .switch-button {
      display: flex;
      align-items: center;

      .iconfont {
        font-size: 20px;
        margin-right: 17px;
        cursor: pointer;
      }
    }
  }

  .body-container {
    padding: 0 24px 10px;

    .date-select-container {
      .month-label {
        height: 17px;
        font-size: 15px;
        line-height: 17px;
        color: #606266;
      }

      .date-select-body {
        display: flex;
        padding-top: 11px;

        .date-list-item {
          flex: 1;
          text-align: center;

          .label {
            height: 20px;
            font-size: 14px;
            line-height: 20px;
            color: #909399 ;
          }

          .day-container {
            margin-top: 9px;
            height: 48px;
            background: #F1FbFF;
            padding-top: 12px;

            .day {
              width: 22px;
              height: 22px;
              margin: auto;
              font-size: 16px;
              line-height: 22px;
              font-weight: bold;
              border-radius: 3px;
              cursor: pointer;
              color: #606266;

              &.select {
                background: #0096CC;
                color: #FFFFFF;
                width: 25px;
                height: 25px;

                .number-icon {
                  .icon {
                    background: #FFFFFF;
                  }
                }
              }

              .number-icon {
                display: flex;
                justify-content: center;
                margin-top: -2px;

                .icon {
                  display: none;
                  width: 3px;
                  height: 3px;
                  border-radius: 50%;
                  background: #0096CC;
                  margin-right: 2px;

                  &.show {
                    display: block;

                    &:last-child {
                      margin-right: 0;
                    }
                  }
                }
              }
            }
          }

          &:first-child {
            .day-container {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
          }

          &:last-child {
            .day-container {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
        }
      }
    }

    .interviews-list {
      margin-top: 12px;

      .interview-list-body {
        .date-label {
          margin-bottom: 8px;
          height: 21px;
          font-size: 15px;
          color: #606266;
          line-height: 21px;
        }

        .interview-list-item {
          background: #FFFFFF;
          box-shadow: 0px 2px 5px -3px rgba(0,0,0,0.12);
          border-radius: 6px;
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          margin-bottom: 8px;
          position: relative;

          &:hover {
            cursor: pointer;
            background: #F9F9F9;
            box-shadow: 0px 2px 5px -3px rgba(0,0,0,0.12), 0px 5px 12px 0px rgba(0,0,0,0.08);
          }

          > span {
            color: #606266;
            font-size: 14px;
            margin-bottom: 2px;
            line-height: 20px;
            height: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            white-space: nowrap;
            word-break: keep-all;
          }

          .el-icon-arrow-right {
            position: absolute;
            right: 9px;
            top: 40%;
          }
        }
      }
    }
  }
}

#main {
  .loading {
    position: absolute;
    z-index: 1;
  }
}
</style>
